export interface ExtractionParams {
  file: File;
  minDuration: number;
  tolerance: number;
  padding?: number;
  emailId: string;
  isPaid: boolean;
}

export interface YouTubeExtractionParams {
  url: string;
  minDuration: number;
  tolerance: number;
  padding?: number;
  emailId: string;
  isPaid: boolean;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
import { supabase } from '@/integrations/supabase/client';

class MusicExtractionApiService {
  // Single source of truth for obtaining the current Supabase access token.
  // Throws if there is no active session.
  private async getAccessToken(): Promise<string> {
    const { data } = await supabase.auth.getSession();
    const session = data?.session;
    const token = session?.access_token;
    if (!token) {
      throw new Error('Not authenticated with Supabase');
    }
    return token;
  }

  // Wrapper around fetch that injects the Bearer token and retries once on 401.
  private async fetchWithBearer(input: RequestInfo, init: RequestInit = {}): Promise<Response> {
    const token = await this.getAccessToken();
    init.headers = {
      ...(init.headers || {}),
      Authorization: `Bearer ${token}`,
    } as Record<string, string>;

    let response = await fetch(input, init);

    // If unauthorized, attempt one refresh (getSession may refresh using stored refresh token)
    if (response.status === 401) {
      try {
        const { data } = await supabase.auth.getSession();
        const newToken = data?.session?.access_token;
        if (newToken && newToken !== token) {
          init.headers = {
            ...(init.headers || {}),
            Authorization: `Bearer ${newToken}`,
          } as Record<string, string>;
          response = await fetch(input, init);
        }
      } catch (err) {
        // swallow and return original 401 response
      }
    }

    return response;
  }

  async healthCheck(): Promise<{ status: string; service: string }> {
    const response = await fetch(`${API_BASE_URL}/health`);
    if (!response.ok) {
      throw new Error('Health check failed');
    }
    return response.json();
  }

  async extractMusicDownload(params: ExtractionParams): Promise<{ job_id: string; message: string; priority: string; status: string; }> {
    const formData = new FormData();
    formData.append('file', params.file);
    formData.append('email_id', params.emailId);
    formData.append('is_paid', String(params.isPaid));
    formData.append('min_duration', params.minDuration.toString());
    formData.append('tolerance', params.tolerance.toString());
    if (params.padding) {
      formData.append('padding', params.padding.toString());
    }

    const response = await this.fetchWithBearer(`${API_BASE_URL}/extract_queue`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('No music segments found in the video');
      }
      if (response.status === 413) {
        throw new Error('File too large (maximum 5GB)');
      }
      if (response.status === 401) {
        throw new Error('Authentication failed');
      }
      throw new Error('Failed to queue extraction');
    }

    return response.json();
  }

  async extractMusicFromYouTube(params: YouTubeExtractionParams): Promise<{ job_id: string; message: string; priority: string; status: string; }> {
    const requestBody = {
      url: params.url,
      email_id: params.emailId,
      is_paid: params.isPaid,
      min_duration: params.minDuration,
      tolerance: params.tolerance,
      ...(params.padding && { padding: params.padding }),
    };

    const response = await this.fetchWithBearer(`${API_BASE_URL}/extract_queue/url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('No music segments found in the video');
      }
      if (response.status === 401) {
        throw new Error('Authentication failed');
      }
      if (response.status === 400) {
        throw new Error('Invalid URL or parameters');
      }
      throw new Error('Failed to queue extraction from YouTube');
    }

    return response.json();
  }
}

export const musicExtractionApi = new MusicExtractionApiService();
